import React from 'react';
import useMapParams from '../../../hooks/useMapParams';
import { useTranslation } from 'react-i18next';

import NestedCommoditySelector from '../NestedCommoditySelector';
import { StatusDropdown } from '../home/WeatherMapControls';
import useCommodityParam from '../../../hooks/useCommodityParam';
import { STATUS_VALUES, parseStatusParam, setStatusParam } from '../../../utils/mapUrlParams';
import AnalyticsChartFullscreenButton from '../analytics/AnalyticsChartFullscreenButton';
import PartySelect from './PartySelect';

/**
 * Persistent global filter strip. Single source of truth for commodity (single-select)
 * and status (multi-select) on the Global Produce Map. Both bind to URL params so deep
 * links from Analytics scorecards work and refresh preserves state.
 * Party selects (importer always; exporter in Shipments) are render-only — options
 * come from the active response once the active response carries them.
 */
const GlobalFilterStrip = ({
  regions = [],
  isFullscreen = false,
  onEnterFullscreen,
  onExitFullscreen,
  showFullscreen = true,
  // 'stacked' (default) = the Analytics filter-bar look, shared by every strip
  // in the app (unfilled 32px control, #E2E2E2 border, 12px text) — the Status
  // dropdown beside it uses the matching variant. 'floating' is the legacy Map
  // look (label inside the control, #B1B9B4 border), kept for callers that
  // still want it.
  commodityVariant = 'stacked',
  // Lets the mobile top-bar mount size the commodity control to the hamburger
  // button next to it. Default keeps the 32px filter-row height.
  commodityHeightClass,
  // Rendered inside the left control group on the map strip (the container
  // search). Passed in rather than mounted here so it keeps its existing
  // wiring to the map's shipment selection.
  searchSlot = null,
  // 'map-mobile' is the Global Produce Map's floating phone stack: hamburger +
  // commodity + fullscreen on the first row, Status + search on the second.
  // Every other caller (ProcurementPlanner, the desktop map strip) keeps the
  // single-row layout below.
  layout = 'row',
  // The drawer hamburger, mounted by the page so this component stays
  // route-agnostic. 'map-mobile' only.
  menuSlot = null,
  // 'map-mobile' only: the 44px search. Falls back to `searchSlot`.
  mobileSearchSlot = null,
  // Growing surfaces production status as a chip in the row below instead, and
  // both write the same `status` URL param. Showing the dropdown too would put
  // one filter behind two controls, which is how they end up disagreeing.
  showStatus = true,
  // Fullscreen only: the filter chips, which normally sit in their own row,
  // join this one beside the search. Desktop-only by construction — the phone
  // layout above has no room for them and keeps its own row.
  chipsSlot = null,
  // Importer / exporter party filters (from dev). Additive to the layout
  // above: they join the same left-hand control group as commodity and status.
  partyReady = false,
  importer = '',
  importerOptions = [],
  onImporterChange,
  showExporter = false,
  exporter = '',
  exporterOptions = [],
  onExporterChange,
}) => {
  const { t } = useTranslation();
  const [commodity, setCommodity] = useCommodityParam();
  const [searchParams, setSearchParams] = useMapParams();

  // Default = all statuses when param absent or invalid (avoids silent filtering;
  // matches the default applied inside UnifiedMapContainer).
  const parsed = parseStatusParam(searchParams);
  const visibleStatuses = parsed.length > 0 ? parsed : STATUS_VALUES;

  const handleStatusChange = (next) => setStatusParam(searchParams, setSearchParams, next);

  const fullscreenButton = showFullscreen && onEnterFullscreen && onExitFullscreen ? (
    <AnalyticsChartFullscreenButton
      compact
      round={commodityVariant === 'map'}
      heightClass="h-10"
      isActive={isFullscreen}
      onEnter={onEnterFullscreen}
      onExit={onExitFullscreen}
    />
  ) : null;

  // Phone layout: one wrapping row of 40px `map` controls floating over the
  // map, so they read as one set with the chips below.
  //
  // One row rather than two fixed ones. This layout covers everything under
  // lg, which is a 360px phone and a half-width desktop window alike: at the
  // wide end the lookup sat on a line of its own with the map showing through
  // beside the commodity selector. `flex-wrap` lets the controls share a line
  // whenever the width allows and fall back to stacking when it does not —
  // the search carries `min-w-[260px]`, so it is the first thing to drop to a
  // second line. The fullscreen toggle is out of flow (see below), so it holds
  // the top-right corner however the rest wraps.
  if (layout === 'map-mobile') {
    return (
      <div className="relative isolate z-[1300] flex w-full flex-wrap items-center gap-[8px] pr-[48px]">
        {menuSlot}
        {/* The commodity column used to be `1fr`, so it swallowed every pixel
            between the hamburger and the fullscreen button — a 180px control
            stretched across the whole phone with its name alone at one end.
            It sizes to the control instead, and the free space goes to the
            auto margin before the fullscreen button. */}
        <div className="min-w-0 shrink">
          <NestedCommoditySelector
            variant="map"
            value={commodity}
            onChange={setCommodity}
            placeholder={t('map.commodityPlaceholder', 'All commodities')}
          />
        </div>
        {showStatus && (
          <StatusDropdown
            variant="map"
            heightClass="h-[44px]"
            regions={regions}
            visibleStatuses={visibleStatuses}
            onStatusesChange={handleStatusChange}
          />
        )}
        {/* Capped rather than free-growing: a container number is short, and a
            full-width field made the row read as the page's main input when it
            is a lookup. `min-w-[260px]` is also the wrap threshold — wider
            than the field's bare minimum on purpose, so a lookup squeezed thin
            beside the commodity selector takes its own line instead. */}
        <div className="min-w-[260px] flex-1 max-w-[320px]">{mobileSearchSlot ?? searchSlot}</div>
        {/* Absolute, not a flex item: as the last child it rode the wrap and
            dropped to whichever line the search ended on. The design keeps it
            in the strip's top-right corner whatever else wraps, and the row's
            `pr-[48px]` is the space it occupies. */}
        <div className="absolute right-0 top-0 shrink-0">{fullscreenButton}</div>
      </div>
    );
  }

  return (
    // Mobile (<lg): only the commodity selector and the fullscreen toggle live
    // in this row — Status moves next to the map controls inside
    // UnifiedMapContainer's mobile "Map controls" panel instead (avoids a
    // second dropdown crowding this already-tight row next to the hamburger).
    // Commodity is first in DOM order (top-left, beside the hamburger) and
    // the fullscreen button's `ml-auto` pushes it to the top-right of the
    // same row once Status is hidden. Desktop (lg+): Status is restored via
    // `lg:block`, exactly the original three-item layout.
    // Centre alignment is right again now that the hoisted chip row is a
    // single line (see MapFilterChips' `singleLine`). It was the wrapped
    // two-line chip block that made centring look broken — the commodity and
    // search were being centred against a block twice their height — and the
    // fix is the chips not wrapping, not the row abandoning its alignment.
    <div className="relative isolate z-[1300] flex w-full shrink-0 flex-wrap items-center gap-2 sm:gap-3 lg:flex-nowrap lg:justify-between lg:gap-[12px]">
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3 lg:flex-nowrap lg:gap-[12px]">
      {/* The mobile top-bar mount (the only caller passing a height) sizes to
          its content beside the hamburger; the desktop strip keeps its own
          fixed column width. */}
      {/* `w-fit` below lg: the trigger inside is `w-full min-w-[180px]`, so
          given a stretchy box it ran to the far side of the phone with the
          commodity name floating at one end. Fitting the box to the trigger's
          own minimum keeps it at the 180px it was designed for. Desktop keeps
          `lg:w-auto`, where the row has room and the strip is laid out. */}
      <div className={commodityHeightClass ? 'w-fit max-w-full min-w-[180px]' : 'w-fit min-w-[180px] max-w-full sm:min-w-[200px] lg:min-w-0 lg:w-auto'}>
        <NestedCommoditySelector
          variant={commodityVariant}
          {...(commodityHeightClass ? { heightClass: commodityHeightClass } : {})}
          value={commodity}
          onChange={setCommodity}
          placeholder={t('map.commodityPlaceholder', 'All commodities')}
        />
      </div>

      {showStatus && (
        <div className="hidden lg:block">
          <StatusDropdown
            variant={commodityVariant === 'map' ? 'map' : 'stacked'}
            regions={regions}
            visibleStatuses={visibleStatuses}
            onStatusesChange={handleStatusChange}
          />
        </div>
      )}

      {/* Shown on mobile too when partyReady — saved/URL party filters still
          hit the active request, so hiding the control would trap users. */}
      {partyReady ? (
        <PartySelect
          label={t('map.filters.importer', 'Importer')}
          value={importer}
          options={importerOptions}
          onChange={onImporterChange}
          allLabel={t('map.filters.allImporters', 'All')}
        />
      ) : null}

      {partyReady && showExporter ? (
        <PartySelect
          label={t('map.filters.exporter', 'Exporter')}
          value={exporter}
          options={exporterOptions}
          onChange={onExporterChange}
          allLabel={t('map.filters.allExporters', 'All')}
        />
      ) : null}

        {/* The 206px is a fixed column, so rendering it empty reserved the
            width anyway. Weather mode has no container search — nothing on
            the map for it to find — so in fullscreen, where the chips are
            hoisted into this row, that dead column pushed the layer switch
            206px clear of Status. Only weather is affected: every other mode
            passes a search and keeps the column. */}
        {commodityVariant === 'map' && searchSlot ? (
          <div className="hidden lg:block lg:w-[206px]">
            {searchSlot}
          </div>
        ) : null}

        {/* `ml-[6px]` on top of the row's own 12px gap puts the chips 18px from
            the search, inside the 16-20px the review asks for. It was 40px
            (ml-28) before — far enough that the chips read as a separate group
            rather than a continuation of the filters. */}
        {/* `min-w-0` lets the chips row shrink and scroll inside the strip
            rather than forcing the row wider than the screen. */}
        {chipsSlot ? (
          <div className="hidden min-w-0 flex-1 lg:block lg:ml-[6px]">{chipsSlot}</div>
        ) : null}
      </div>

      {fullscreenButton ? <div className="ml-auto lg:ml-0">{fullscreenButton}</div> : null}
    </div>
  );
};

export default GlobalFilterStrip;
